import { BASEURL } from "../BaseUrl";

/**
 * Creates a new engagement with the provided details.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The details of the engagement to create.
 * @param {string} data.engagementName - The name of the new engagement.
 * @param {string} data.userId - The ID of the user creating the engagement.
 * @param {string} data.clientId - The ID of the client associated with the engagement.
 * @param {string} data.userName - The username of the user creating the engagement.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const createEngagement = async (axios, data) => {
  return await axios.post(`${BASEURL}/engagement/add-engagement`, {
    engagementName: data.engagementName,
    userId: data.userId,
    clientId: data.clientId,
    userName: data.userName,
    assetTag:data.assetTag,
  });
};

/**
 * Executes a data processor for an engagement.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data to execute the data processor with.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const executeEngagementProcessorApi = async (axios, data) => {
  return await axios.post(
    `${BASEURL}/engagement-processor/execute-data-processor`,
    data,
    {}
  );
};

/**
 * Fetches a list of engagements with optional filters.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The parameters to filter the engagement list.
 * @param {string} data.user_id - The ID of the user requesting the engagement list.
 * @param {string} data.client - The client name to filter engagements by.
 * @param {string} data.client_id - The ID of the client to filter engagements by.
 * @param {string} data.search - The search term to filter engagements by name or other attributes.
 * @param {string} data.searchParam - The parameter to search by (e.g., name).
 * @param {string} data.status - The status of the engagements to filter by.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getAllEngagements = async (axios, data) => {
  return await axios.get(`${BASEURL}/engagement/get-engagement`, {
    params: {
      user_id: data.user_id,
      client: data.client,
      client_id: data.client_id,
      search: data.search,
      searchParam: data.searchParam,
      status: data.status,
      pageNumber: data.pageNumber,
      pageSize: data.pageSize,
    },
  });
};

/**
 * Deletes a specific engagement by ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} engagement_id - The ID of the engagement to be deleted.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const deleteEngagement = async (axios, engagement_id) => {
  return await axios.delete(`${BASEURL}/engagement/delete-engagement`, {
    params: { engagement_id },
  });
};

/**
 * Updates the status of a specific engagement.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data containing the engagement ID and new status.
 * @param {string} data.engagement_id - The ID of the engagement whose status is to be updated.
 * @param {string} data.status - The new status to set for the engagement.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const engagementStatusChanged = async (axios, data) => {
  return await axios.put(
    `${BASEURL}/engagement/engagement-status-changed`,
    null,
    {
      params: {
        engagement_id: data.engagement_id,
        status: data.status,
      },
    }
  );
};

export const EngagementApi = {
  createEngagement,
  getAllEngagements,
  deleteEngagement,
  executeEngagementProcessorApi,
  engagementStatusChanged,
};
